import { faWindowClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { withTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import './index.css';

// F005 — new-site opt-in invite (clone of features/CookieConsent).
// `_site=new` is read by nginx `map $cookie__site $afnu_root` to serve the new
// (Vite) build to this visitor. See 3-spec.md §5 for the cookie protocol.
const cookies = new Cookies();
const SITE_COOKIE = '_site';
const DISMISS_COOKIE = '_invite_dismissed';

class NewWebsiteBanner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Hidden once dismissed (this session) or if already opted in.
      visible: !cookies.get(DISMISS_COOKIE) && cookies.get(SITE_COOKIE) !== 'new',
      // True while a category deck is loading — keeps the invite hidden on mobile
      // until the deck is revealed (see Category.js `emitDeckLoading`).
      deckLoading: false,
    };
    this.handleOptIn = this.handleOptIn.bind(this);
    this.handleDismiss = this.handleDismiss.bind(this);
    this.onDeckLoading = this.onDeckLoading.bind(this);
  }

  componentDidMount() {
    window.addEventListener('afnu:deck-loading', this.onDeckLoading);
    // The category deck mounts before this global banner, so its first
    // "loading" event is missed — sync the initial state from the live spinner.
    if (document.querySelector('.preloader.show-item')) {
      this.setState({ deckLoading: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('afnu:deck-loading', this.onDeckLoading);
  }

  onDeckLoading(e) {
    this.setState({ deckLoading: !!(e && e.detail && e.detail.loading) });
  }

  handleOptIn() {
    // Domain/Secure only on afnu.co.il hosts — a `.afnu.co.il` Domain on
    // localhost/IP is rejected, so the local harness uses a host-only cookie.
    // Mirrors the new site's lib/cookies.ts.
    const isAfnu = window.location.hostname.endsWith('afnu.co.il');
    cookies.set(SITE_COOKIE, 'new', {
      path: '/',
      maxAge: 31536000,
      sameSite: 'lax',
      ...(isAfnu ? { domain: '.afnu.co.il', secure: true } : {}),
    });
    // Best-effort adoption funnel event before the reload swaps the build.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'new_site_opt_in' });
    window.setTimeout(() => window.location.reload(), 150);
  }

  handleDismiss() {
    // Hide immediately so the close can never be blocked; then persist a session
    // cookie (no maxAge/expires) → invite reappears next browser session.
    this.setState({ visible: false });
    try {
      cookies.set(DISMISS_COOKIE, '1', { path: '/' });
    } catch (e) {
      /* ignore cookie errors — banner is already hidden */
    }
  }

  render() {
    const { t } = this.props;
    if (!this.state.visible) {
      return null;
    }

    // Mobile only (matches this banner's own mobile breakpoint): stay hidden while
    // the deck spinner shows, then fade in once the deck data is revealed.
    const isMobile =
      typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches;
    const hiddenForLoading = isMobile && this.state.deckLoading;

    return (
      <div
        className={'new-website-banner' + (hiddenForLoading ? ' nwb-loading-hidden' : '')}
        role="region"
        aria-label={t('newWebsiteBanner.message')}
      >
        <span className="new-website-banner-text">{t('newWebsiteBanner.message')}</span>
        <div className="new-website-banner-actions">
          <button
            type="button"
            className="new-website-banner-cta"
            onClick={this.handleOptIn}
            aria-label={t('newWebsiteBanner.ctaAriaLabel')}
          >
            {t('newWebsiteBanner.cta')}
          </button>
          <button
            type="button"
            className="new-website-banner-decline"
            onClick={this.handleDismiss}
          >
            {t('newWebsiteBanner.declineCta')}
          </button>
          <button
            type="button"
            className="new-website-banner-close"
            onClick={this.handleDismiss}
            aria-label={t('newWebsiteBanner.dismissAriaLabel')}
          >
            <FontAwesomeIcon icon={faWindowClose} />
          </button>
        </div>
      </div>
    );
  }
}

export default withTranslation()(NewWebsiteBanner);
